import React, { useState, useCallback, useMemo, useEffect, Fragment } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import {
  XMarkIcon,
  DocumentIcon,
  PhotoIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowUpTrayIcon,
  FolderIcon,
} from "@heroicons/react/24/outline";
import { useDropzone } from "react-dropzone";
import { Button } from "components/ui";
import clsx from "clsx";
import { toast } from "sonner";
import { uploadLeadDocuments } from "../../../../../services/leadsService";
import { uploadJobDocuments } from "../../../../../services/jobsService";
import {
  uploadDocumentFolderStructure,
  uploadFolderRawFilesToS3,
  DOCUMENT_ENTITY_TYPES,
} from "services/documentFolderService";

// File type configurations
const FILE_TYPES = {
  PDF: {
    extensions: [".pdf"],
    icon: DocumentIcon,
    color: "text-red-600",
    bgColor: "bg-red-50",
  },
  DOCX: {
    extensions: [".docx", ".doc"],
    icon: DocumentIcon,
    color: "text-blue-600",
    bgColor: "bg-blue-50",
  },
  EXCEL: {
    extensions: [".xlsx", ".xls", ".csv"],
    icon: DocumentIcon,
    color: "text-emerald-600",
    bgColor: "bg-emerald-50",
  },
  POWERPOINT: {
    extensions: [".pptx", ".ppt"],
    icon: DocumentIcon,
    color: "text-orange-600",
    bgColor: "bg-orange-50",
  },
  IMAGE: {
    extensions: [".jpg", ".jpeg", ".png"],
    icon: PhotoIcon,
    color: "text-green-600",
    bgColor: "bg-green-50",
  },
};

const DEFAULT_MAX_FILE_SIZE = undefined;
const DEFAULT_MAX_FILES = 30;

const ACCEPTED_FORMATS = {
  "application/pdf": [".pdf"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
  "application/msword": [".doc"],
  "application/vnd.ms-excel": [".xls"],
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
  "text/csv": [".csv"],
  "application/vnd.ms-powerpoint": [".ppt"],
  "application/vnd.openxmlformats-officedocument.presentationml.presentation": [".pptx"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

const ALLOWED_EXTENSIONS = [
  ".pdf",
  ".doc",
  ".docx",
  ".xls",
  ".xlsx",
  ".csv",
  ".ppt",
  ".pptx",
  ".jpg",
  ".jpeg",
  ".png",
];

const BLOCKED_EXTENSIONS = [
  ".jfif",
  ".exe",
  ".bat",
  ".cmd",
  ".sh",
  ".vbs",
  ".js",
  ".jsx",
  ".ts",
  ".tsx",
  ".dll",
  ".msi",
  ".scr",
  ".jar",
  ".apk",
  ".com",
  ".ps1",
  ".tmp",
  ".bak",
  ".sys",
  ".ini",
  ".lnk",
];

const fileValidator = (file) => {
  const ext = "." + file.name.split(".").pop().toLowerCase();

  if (BLOCKED_EXTENSIONS.includes(ext) || !ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      code: "file-invalid-extension",
      message: `File type "${ext}" is not supported`,
    };
  }

  return null;
};




// Utility function to sanitize filenames
const sanitizeFileName = (fileName) => {
  // Remove special characters and replace spaces with underscores
  return fileName
    .replace(/[^a-zA-Z0-9.\-_]/g, "_")
    .replace(/_{2,}/g, "_")
    .toLowerCase();
};

// Utility function to get file type category
const getFileTypeCategory = (fileName) => {
  const extension = "." + fileName.split(".").pop().toLowerCase();

  for (const [category, config] of Object.entries(FILE_TYPES)) {
    if (config.extensions.includes(extension)) {
      return { category, config };
    }
  }
  return { category: "UNKNOWN", config: FILE_TYPES.PDF };
};

// Utility function to format file size
const formatFileSize = (bytes) => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
};

// Helper to parse backend validation errors (FileResults & general Errors)
const parseValidationResponse = (rawResponse) => {
  let fileErrorMap = new Map();
  let fileValidMap = new Map();
  let errorMessage = "Upload failed";

  if (!rawResponse) return { errorMessage, fileErrorMap, fileValidMap };

  let targetObj = rawResponse;
  if (targetObj.response?.data) targetObj = targetObj.response.data;

  // Extract nested data payload if JSON stringified or object
  if (targetObj.data) {
    let payload = targetObj.data;
    if (typeof payload === "string") {
      try {
        payload = JSON.parse(payload);
      } catch (e) {
        // ignore parse error
      }
    }
    if (typeof payload === "object" && payload !== null) {
      targetObj = { ...targetObj, ...payload };
    }
  }

  if (targetObj.message) errorMessage = targetObj.message;
  if (targetObj.error) errorMessage = targetObj.error;

  if (targetObj.Errors && Array.isArray(targetObj.Errors) && targetObj.Errors.length > 0) {
    const uniqueErrors = [...new Set(targetObj.Errors.filter(Boolean))];
    if (uniqueErrors.length > 0) {
      errorMessage = uniqueErrors.join(", ");
    }
  }

  const fileResults = targetObj.FileResults || targetObj.fileResults || [];
  if (Array.isArray(fileResults) && fileResults.length > 0) {
    fileResults.forEach((fr) => {
      const fileName = (fr.FileName || fr.fileName || "").toLowerCase();
      const isValid = fr.IsValid ?? fr.isValid ?? (fr.Errors && fr.Errors.length === 0);

      if (fileName) {
        if (isValid) {
          fileValidMap.set(fileName, true);
        } else {
          fileValidMap.set(fileName, false);
          const errs = fr.Errors && fr.Errors.length > 0
            ? fr.Errors.join(", ")
            : fr.message || "File validation failed";
          fileErrorMap.set(fileName, errs);
        }
      }
    });
  }

  return { errorMessage, fileErrorMap, fileValidMap };
};

const applyFileStatuses = (filesList, errorMessage, fileErrorMap, fileValidMap) => {
  const hasFileResults = fileValidMap.size > 0 || fileErrorMap.size > 0;

  return filesList.map((f) => {
    const fileNameKey = f.file.name.toLowerCase();
    const isExplicitValid = fileValidMap.get(fileNameKey);
    const specificError = fileErrorMap.get(fileNameKey);

    if (isExplicitValid === true) {
      return {
        ...f,
        status: "success",
        progress: 100,
        error: null,
      };
    } else if (specificError) {
      return {
        ...f,
        status: "error",
        progress: 0,
        error: specificError,
      };
    } else if (hasFileResults) {
      return {
        ...f,
        status: "success",
        progress: 100,
        error: null,
      };
    } else {
      return {
        ...f,
        status: "error",
        progress: 0,
        error: errorMessage || "Failed to upload",
      };
    }
  });
};

export function UploadDocumentModal({
  isOpen,
  onClose,
  onUploadSuccess,
  leadId,
  folderId,
  jobId,
  uploadType = "file",
  initialFiles = null,
  refreshProgressJourney = () => { },
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
  maxFiles = DEFAULT_MAX_FILES,
}) {
  const [files, setFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize or append files state when initialFiles prop is provided
  useEffect(() => {
    if (isOpen && Array.isArray(initialFiles) && initialFiles.length > 0) {
      setFiles((prev) => {
        const existingKeys = new Set(
          prev.map((f) => `${f.sanitizedName}_${f.size}`)
        );
        const toAdd = initialFiles.filter(
          (file) => !existingKeys.has(`${sanitizeFileName(file.name)}_${file.size}`)
        );
        if (toAdd.length === 0) return prev;

        const formattedFiles = toAdd.map((file) => {
          const objectUrl = file.type?.startsWith("image/") ? URL.createObjectURL(file) : null;
          return {
            id: Math.random().toString(36).substr(2, 9),
            file: file,
            sanitizedName: sanitizeFileName(file.name),
            size: file.size,
            type: file.type,
            status: "pending",
            progress: 0,
            error: null,
            objectUrl: objectUrl,
          };
        });

        return [...prev, ...formattedFiles];
      });
    }
  }, [isOpen, initialFiles]);

  const detectedFolderName = useMemo(() => {
    if (files.length === 0) return null;
    const sampleWithRelPath = files.find((f) => f.file?.webkitRelativePath);
    if (sampleWithRelPath && sampleWithRelPath.file.webkitRelativePath) {
      const parts = sampleWithRelPath.file.webkitRelativePath.split(/[\/\\]/);
      if (parts.length > 1 && parts[0]) {
        return parts[0];
      }
    }
    return uploadType === "folder" ? "Selected Folder" : null;
  }, [files, uploadType]);

  const totalFolderSize = useMemo(() => {
    return files.reduce((acc, curr) => acc + (curr.size || 0), 0);
  }, [files]);

  const isFolderMode = uploadType === "folder" || Boolean(detectedFolderName);

  // Cleanup object URLs when component unmounts or modal closes
  useEffect(() => {
    return () => {
      // Cleanup all object URLs when component unmounts
      files.forEach((fileData) => {
        if (fileData.objectUrl) {
          URL.revokeObjectURL(fileData.objectUrl);
        }
      });
    };
  }, []);

  // Cleanup when modal closes
  useEffect(() => {
    if (!isOpen && files.length > 0) {
      files.forEach((fileData) => {
        if (fileData.objectUrl) {
          URL.revokeObjectURL(fileData.objectUrl);
        }
      });
      setFiles([]);
    }
  }, [isOpen]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      rejectedFiles.forEach((rejectedFile) => {
        const { file, errors } = rejectedFile;
        errors.forEach((error) => {
          let message = `File "${file.name}" was rejected: `;
          if (error.code === "file-too-large") {
            message += `File size exceeds ${formatFileSize(maxFileSize)} limit`;
          } else if (error.code === "file-invalid-type") {
            message += "File format not supported";
          } else if (error.code === "file-invalid-extension") {
            message += error.message;
          } else {
            message += error.message;
          }
          toast.error(message);
        });
      });
    }

    // Process accepted files
    if (acceptedFiles.length > 0) {
      setFiles((prev) => {
        const existingKeys = new Set(
          prev.map((f) => `${f.sanitizedName}_${f.size}`)
        );
        const toAdd = acceptedFiles.filter(
          (file) => !existingKeys.has(`${sanitizeFileName(file.name)}_${file.size}`)
        );
        if (toAdd.length === 0) {
          toast.info("Selected file(s) are already added");
          return prev;
        }

        const newFiles = toAdd.map((file) => {
          const objectUrl = file.type.startsWith("image/")
            ? URL.createObjectURL(file)
            : null;

          return {
            id: Math.random().toString(36).substr(2, 9),
            file: file,
            sanitizedName: sanitizeFileName(file.name),
            size: file.size,
            type: file.type,
            status: "pending", // pending, uploading, success, error
            progress: 0,
            error: null,
            objectUrl: objectUrl, // Store object URL for cleanup
          };
        });

        return [...prev, ...newFiles];
      });
    }
  }, [maxFileSize]);

  const dropzoneProps = useMemo(() => {
    const baseProps = {
      onDrop,
      multiple: true,
      noClick: false,
      noKeyboard: true,
      validator: fileValidator,
    };
    if (maxFileSize) {
      baseProps.maxSize = maxFileSize;
    }
    if (uploadType !== "folder") {
      baseProps.accept = ACCEPTED_FORMATS;
    }
    return baseProps;
  }, [onDrop, uploadType, maxFileSize]);

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone(dropzoneProps);

  const removeFile = (fileId) => {
    setFiles((prev) => {
      const fileToRemove = prev.find((file) => file.id === fileId);
      if (fileToRemove && fileToRemove.objectUrl) {
        URL.revokeObjectURL(fileToRemove.objectUrl);
      }
      return prev.filter((file) => file.id !== fileId);
    });
  };

  const clearAllFiles = () => {
    files.forEach((fileData) => {
      if (fileData.objectUrl) {
        URL.revokeObjectURL(fileData.objectUrl);
      }
    });
    setFiles([]);
  };

  const validateFiles = () => {
    if (files.length === 0) {
      toast.error("Please select at least one file to upload");
      return false;
    }
    if (files.length > maxFiles) {
      toast.error(`Maximum ${maxFiles} files can be uploaded at a time`);
      return false;
    }
    return true;
  };

  const handleUpload = async () => {
    if (!validateFiles()) return;

    setIsUploading(true);

    try {
      // Mark all files as uploading
      setFiles((prev) =>
        prev.map((f) => ({ ...f, status: "uploading", progress: 50 }))
      );

      // Get the actual files from the file data
      const actualFiles = files.map((fileData) => fileData.file);

      // Call the API to upload documents
      let response;
      const isFolderUpload = uploadType === "folder" || files.some((f) => f.file?.webkitRelativePath);

      if (isFolderUpload) {
        const presignedItems = await uploadFolderRawFilesToS3({
          entityType: jobId ? DOCUMENT_ENTITY_TYPES.JOB : DOCUMENT_ENTITY_TYPES.LEAD,
          entityId: jobId || leadId,
          parentFolderId: folderId,
          files: actualFiles,
        });

        const folderUploadItems = files.map((fileData, idx) => {
          const relPath = fileData.file?.webkitRelativePath || fileData.file?.name;
          const name = fileData.file?.name || "file";
          const ext = "." + name.split(".").pop().toLowerCase();
          const uploadedFilePath = presignedItems[idx]?.fileKey || presignedItems[idx]?.FileKey || "";
          return {
            relativePath: relPath,
            fileName: name,
            fileExtension: ext,
            fileSize: fileData.size,
            filePath: uploadedFilePath,
            isFolder: false,
          };
        });

        response = await uploadDocumentFolderStructure({
          entityType: jobId ? DOCUMENT_ENTITY_TYPES.JOB : DOCUMENT_ENTITY_TYPES.LEAD,
          entityId: jobId || leadId,
          parentFolderId: folderId,
          files: folderUploadItems,
        });
      } else if (jobId) {
        response = await uploadJobDocuments(jobId, actualFiles, folderId);
      } else {
        response = await uploadLeadDocuments(leadId, actualFiles, folderId);
      }

      if (response.success) {
        // Mark all files as successfully uploaded
        setFiles((prev) =>
          prev.map((f) => ({ ...f, status: "success", progress: 100 })),
        );

        const uploadedFilesData = files.map((fileData) => ({
          name: fileData.sanitizedName,
          originalName: fileData.file.name,
          size: fileData.size,
          type: fileData.type,
          file: fileData.file,
          ...(jobId ? { jobId } : { leadId }),
        }));
        if (!jobId) {
          refreshProgressJourney();
        }

        // Show success toast
        const uploadSuccessMsg = isFolderMode && detectedFolderName
          ? `Folder "${detectedFolderName}" (${uploadedFilesData.length} files) uploaded successfully!`
          : `${uploadedFilesData.length} file${uploadedFilesData.length > 1 ? "s" : ""} uploaded successfully!`;

        toast.success(uploadSuccessMsg);

        // Call success callback
        if (onUploadSuccess) {
          onUploadSuccess(uploadedFilesData);
        }

        // Close modal after successful upload
        setTimeout(() => {
          onClose();
          setFiles([]);
          setIsUploading(false);
        }, 1500);
      } else {
        const { errorMessage, fileErrorMap, fileValidMap } = parseValidationResponse(response.data || response);

        setFiles((prev) => applyFileStatuses(prev, errorMessage, fileErrorMap, fileValidMap));

        toast.error(errorMessage || "File validation failed");
        setIsUploading(false);
      }
    } catch (error) {
      console.error("Upload error:", error);

      const errData = error.response?.data || error.data;
      const { errorMessage, fileErrorMap, fileValidMap } = parseValidationResponse(errData);

      const finalErrMsg = errorMessage !== "Upload failed" ? errorMessage : (error.message || "Failed to upload files");

      setFiles((prev) => applyFileStatuses(prev, finalErrMsg, fileErrorMap, fileValidMap));

      toast.error(finalErrMsg);
      setIsUploading(false);
    }
  };


  const renderFilePreview = (fileData) => {
    const { file, sanitizedName, size, status, progress, objectUrl } = fileData;
    const { category, config } = getFileTypeCategory(file.name);
    const Icon = config.icon;
    const isImage = category === "IMAGE";

    return (
      <div
        key={fileData.id}
        className={clsx(
          "group relative flex items-center gap-3 rounded-lg border p-3 transition-all min-w-0",
          status === "success"
            ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20"
            : status === "error"
              ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20"
              : "border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800",
        )}
      >
        {/* File Icon or Image Preview */}
        <div className="relative shrink-0">
          {isImage && objectUrl ? (
            <div className="relative h-12 w-12 overflow-hidden rounded-md">
              <img
                src={objectUrl}
                alt={sanitizedName}
                className="h-full w-full object-cover"
                onError={(e) => {
                  // Fallback to icon if image fails to load
                  e.target.style.display = "none";
                  e.target.parentElement.innerHTML =
                    '<div class="flex h-12 w-12 items-center justify-center rounded-md bg-gray-100 dark:bg-gray-700"><svg class="h-6 w-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg></div>';
                }}
              />
            </div>
          ) : (
            <div
              className={clsx(
                "flex h-12 w-12 items-center justify-center rounded-md",
                config.bgColor,
              )}
            >
              <Icon className={clsx("h-6 w-6", config.color)} />
            </div>
          )}

          {/* Status Icon */}
          {status === "success" && (
            <CheckCircleIcon className="absolute -top-1 -right-1 h-5 w-5 text-green-600" />
          )}
          {/* {status === "error" && (
            <ExclamationTriangleIcon className="absolute -top-1 -right-1 h-5 w-5 text-red-600" />
          )} */}
        </div>

        {/* File Info */}
        <div className="min-w-0 flex-1">
          <p
            className="truncate text-sm font-medium text-gray-900 dark:text-gray-100"
            title={file.name || sanitizedName}
          >
            {sanitizedName}
          </p>
          {file.webkitRelativePath && file.webkitRelativePath.includes("/") && (
            <p
              className="truncate text-[11px] font-medium text-amber-700 dark:text-amber-400"
              title={file.webkitRelativePath}
            >
              📁 {file.webkitRelativePath.substring(file.webkitRelativePath.indexOf("/") + 1)}
            </p>
          )}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {formatFileSize(size)}
          </p>
          {status === "uploading" && (
            <div className="mt-1">
              <div className="h-1 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
                <div
                  className="bg-primary-600 h-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Uploading... {progress}%
              </p>
            </div>
          )}
          {status === "error" && fileData.error && (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400 break-words">
              {fileData.error}
            </p>
          )}
        </div>

        {/* Remove Button */}
        {!isUploading && status !== "success" && (
          <button
            onClick={() => removeFile(fileData.id)}
            className="shrink-0 opacity-100 cursor-pointer transition-opacity p-1 rounded-md hover:bg-red-50 dark:hover:bg-red-900/20"
            disabled={isUploading}
            title="Remove file"
          >
            <TrashIcon className="h-4 w-4 text-red-400 hover:text-red-600" />
          </button>
        )}
      </div>
    );
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[300] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={null}
        onDragEnter={(e) => { e.stopPropagation(); }}
        onDragOver={(e) => { e.stopPropagation(); }}
        onDragLeave={(e) => { e.stopPropagation(); }}
        onDrop={(e) => { e.stopPropagation(); }}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                {isFolderMode ? (
                  <FolderIcon className="h-5 w-5 text-amber-500" />
                ) : (
                  <ArrowUpTrayIcon className="h-5 w-5 text-blue-600" />
                )}
                {isUploading
                  ? isFolderMode
                    ? `Uploading Folder${detectedFolderName ? ` "${detectedFolderName}"` : ""}...`
                    : "Uploading Documents..."
                  : isFolderMode
                  ? "Upload Folder"
                  : "Upload Documents"}
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
                disabled={isUploading}
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              {/* Dropzone Area */}
              <div
                {...getRootProps()}
                className={clsx(
                  "cursor-pointer rounded-lg border-2 border-dashed p-6 text-center transition-all",
                  isDragActive
                    ? "border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-900/20"
                    : isFolderMode
                    ? "border-amber-300 bg-amber-50/30 hover:border-amber-400 dark:border-amber-700/60 dark:bg-amber-950/10"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 dark:border-gray-600 dark:bg-gray-700/50 dark:hover:border-gray-500",
                  isUploading && "pointer-events-none opacity-75",
                )}
              >
                <input
                  {...getInputProps(
                    uploadType === "folder"
                      ? { webkitdirectory: "true", directory: "true" }
                      : {}
                  )}
                />
                <div className="flex flex-col items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm dark:bg-gray-800">
                    {isFolderMode ? (
                      <FolderIcon className="h-6 w-6 text-amber-500" />
                    ) : (
                      <ArrowUpTrayIcon className="h-6 w-6 text-gray-400" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {isDragActive
                        ? "Drop here"
                        : isFolderMode
                        ? files.length > 0
                          ? "Click or drop to select a different folder"
                          : "Click to select a folder"
                        : "Drag & drop files here"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {isFolderMode
                        ? "or drag & drop a folder"
                        : "or click to select files"}
                    </p>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                    {isFolderMode && (
                      <p>Entire folder contents will be uploaded</p>
                    )}
                    <p>Accepted file types: {ALLOWED_EXTENSIONS.join(", ")}</p>
                    {maxFileSize && (
                      <p>Max size: {formatFileSize(maxFileSize)} per file</p>
                    )}
                    {!isFolderMode && <p>Max {maxFiles} files</p>}
                  </div>
                </div>
              </div>

              {/* Folder Summary Card & File List */}
              {files.length > 0 && (
                <div className="mt-6">
                  {/* Folder Banner Card when in Folder Mode */}
                  {isFolderMode && (
                    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/60 dark:bg-amber-950/30 shadow-2xs">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3.5 min-w-0">
                          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400 border border-amber-200 dark:border-amber-800 shadow-xs">
                            <FolderIcon className="h-6 w-6 stroke-[1.8]" />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 bg-amber-200/60 dark:bg-amber-900/60 px-1.5 py-0.5 rounded">
                                Folder Selected
                              </span>
                            </div>
                            <h4 className="truncate text-base font-semibold text-gray-900 dark:text-gray-100 mt-0.5" title={detectedFolderName || "Folder"}>
                              {detectedFolderName || "Selected Folder"}
                            </h4>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                              <span className="font-medium text-gray-800 dark:text-gray-200">{files.length} file{files.length !== 1 ? "s" : ""}</span>
                              <span className="mx-1.5">•</span>
                              <span>Total size: {formatFileSize(totalFolderSize)}</span>
                            </p>
                          </div>
                        </div>
                        {!isUploading && (
                          <button
                            type="button"
                            onClick={clearAllFiles}
                            className="shrink-0 p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors cursor-pointer rounded-md hover:bg-red-50 dark:hover:bg-red-950/30"
                            title="Remove selected folder"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        )}
                      </div>

                      {/* Overall Folder Upload Progress Indicator */}
                      {isUploading && (
                        <div className="mt-3.5 pt-3 border-t border-amber-200/70 dark:border-amber-900/50">
                          <div className="flex items-center justify-between text-xs font-medium text-amber-900 dark:text-amber-200 mb-1.5">
                            <span className="flex items-center gap-2">
                              <div className="size-3.5 animate-spin rounded-full border-2 border-amber-600 border-t-transparent dark:border-amber-400" />
                              Uploading folder structure & files...
                            </span>
                            <span>
                              {Math.round(
                                (files.filter((f) => f.status === "success").length / files.length) * 100
                              ) || 50}%
                            </span>
                          </div>
                          <div className="h-2 w-full overflow-hidden rounded-full bg-amber-200/80 dark:bg-amber-950">
                            <div
                              className="h-full bg-amber-600 dark:bg-amber-500 transition-all duration-300"
                              style={{
                                width: `${
                                  Math.round(
                                    (files.filter((f) => f.status === "success").length / files.length) * 100
                                  ) || 50
                                }%`,
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="mb-3 flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 flex items-center gap-1.5">
                      {isFolderMode ? (
                        <>
                          <FolderIcon className="h-4 w-4 text-amber-500 shrink-0" />
                          <span>Folder Contents ({files.length} items)</span>
                        </>
                      ) : (
                        `Selected Files (${files.length})`
                      )}
                    </h3>
                    {!isUploading && !isFolderMode && (
                      <button
                        onClick={clearAllFiles}
                        className="text-xs cursor-pointer text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                      >
                        Clear All
                      </button>
                    )}
                  </div>
                  <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                    {files.map((fileData) => renderFilePreview(fileData))}
                  </div>
                </div>
              )}
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                onClick={onClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isUploading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                color="primary"
                className="min-w-[7rem] rounded-full"
                disabled={isUploading || files.length === 0}
                title={
                  files.length === 0
                    ? isFolderMode
                      ? "Please select a folder"
                      : "Please select at least one file"
                    : isFolderMode
                    ? `Upload folder (${files.length} files)`
                    : "Upload selected files"
                }
              >
                {isUploading ? (
                  <>
                    <div className="mr-2 size-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {isFolderMode ? "Uploading Folder..." : "Uploading..."}
                  </>
                ) : (
                  <>
                    {isFolderMode ? (
                      <FolderIcon className="mr-2 size-4" />
                    ) : (
                      <ArrowUpTrayIcon className="mr-2 size-4" />
                    )}
                    {isFolderMode
                      ? `Upload Folder ${files.length > 0 ? `(${files.length})` : ""}`
                      : `Upload ${files.length > 0 ? `(${files.length})` : ""}`}
                  </>
                )}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
}

export default UploadDocumentModal;
